import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { createTheme, ThemeProvider, CssBaseline, Theme } from '@mui/material';
import { ThemeMode, tokens, token } from './tokens';

interface ColorModeContextType {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);

export const useColorMode = (): ColorModeContextType => {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error('useColorMode must be used within a ColorModeProvider');
  return ctx;
};

const STORAGE_KEY = 'nfl-picks-theme';

// Resolve the initial mode: explicit saved choice wins, else follow the OS.
const getInitialMode = (): ThemeMode => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* storage unavailable — fall through to system preference */
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

// Push the token set for `mode` onto :root as CSS variables, and stamp
// data-theme so the hand-rolled CSS (App.css / GameCard.css) swaps too.
const applyCssVariables = (mode: ThemeMode) => {
  const root = document.documentElement;
  const set = tokens[mode];
  Object.keys(set).forEach((name) => root.style.setProperty(name, set[name]));
  root.setAttribute('data-theme', mode);
};

const buildTheme = (mode: ThemeMode): Theme =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: token(mode, '--color-primary'),
        dark: token(mode, '--color-primary-strong'),
        contrastText: token(mode, '--color-primary-contrast'),
      },
      secondary: { main: token(mode, '--color-accent') },
      success: { main: token(mode, '--color-success') },
      warning: { main: token(mode, '--color-warning') },
      error: { main: token(mode, '--color-danger') },
      background: {
        default: token(mode, '--color-bg'),
        paper: token(mode, '--color-surface'),
      },
      text: {
        primary: token(mode, '--color-text'),
        secondary: token(mode, '--color-text-muted'),
      },
      divider: token(mode, '--color-border'),
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    },
  });

export const ColorModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

  useEffect(() => {
    applyCssVariables(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore persistence failures */
    }
  }, [mode]);

  const value = useMemo<ColorModeContextType>(
    () => ({
      mode,
      setMode: setModeState,
      toggle: () => setModeState((m) => (m === 'light' ? 'dark' : 'light')),
    }),
    [mode]
  );

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
